import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, q, explain } from '../lib/supabase';
import { useAsync } from '../lib/useAsync';
import { useGroup } from './GroupLayout';
import { useAuth } from '../lib/AuthContext';
import { recomputeGroupRatings } from '../lib/recompute';
import { suggestManOfTheMatch } from '../lib/motm';
import { EmptyState, ErrorBanner, Spinner } from '../components/Shared';
import GoalModal from '../components/GoalModal';
import { goalsFromTotals, newGoal } from '../lib/goals';
import Icon from '../components/Icon';

/**
 * A player's line on the result sheet: what they did, not how they played.
 *
 * The faces used to live here too, which meant whoever logged the game rated
 * everyone once at submit time and then again on History as a member of the
 * group — one person, two votes, and their second one cast knowing what their
 * first had already done to the average. Rating belongs to everybody, in one
 * place, afterwards.
 */
function PlayerCard({ player, goals, assists }) {
  return (
    <div className="log-card log-card--tight">
      <div className="log-card__head">
        <span className="log-card__name">{player.name}</span>
        <span className="log-card__tally">
          {goals > 0 && <span title={`${goals} goal${goals === 1 ? '' : 's'}`}>⚽ {goals}</span>}
          {assists > 0 && <span title={`${assists} assist${assists === 1 ? '' : 's'}`}>🅰️ {assists}</span>}
          <span className="log-card__pos">{player.position}</span>
        </span>
      </div>
    </div>
  );
}

export default function LogScreen() {
  const { group, isAdmin } = useGroup();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const editingGameId = params.get('game');

  const [goals, setGoals] = useState([]);
  const [addingFor, setAddingFor] = useState(null);
  const [notice, setNotice] = useState('');
  const [motmId, setMotmId] = useState(null);
  const [motmTouched, setMotmTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(null);

  const { data, loading, error, reload } = useAsync(async () => {
    const game = editingGameId
      ? await q(
          supabase
            .from('games')
            .select('id, score_a, score_b, status, created_at')
            .eq('id', editingGameId)
            .eq('group_id', group.id)
            .maybeSingle(),
          'Loading that game'
        )
      : await q(
          supabase
            .from('games')
            .select('id, score_a, score_b, status, created_at')
            .eq('group_id', group.id)
            .eq('status', 'balanced')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          'Looking for a game to log'
        );

    if (!game) return { game: null, players: [] };

    const participants = await q(
      supabase
        .from('game_participants')
        .select(
          'id, member_id, team, position, goals, assists, performance, group_members(display_name, profile_id, profiles(display_name))'
        )
        .eq('game_id', game.id),
      'Loading the players in this game'
    );

    const nameFor = (gm) =>
      (gm?.profile_id ? gm?.profiles?.display_name : null) || gm?.display_name || 'Unnamed';

    const players = (participants || []).map((p) => ({
      id: p.id,
      memberId: p.member_id,
      name: nameFor(p.group_members),
      position: p.position,
      team: p.team,
      goals: p.goals || 0,
      assists: p.assists || 0,
      // Read straight through, no default. Games logged before rating moved
      // to History carry a real number here and must keep it; new ones carry
      // null until the group says otherwise.
      performance: p.performance ?? null,
    }));

    return { game, players };
  }, [group.id, editingGameId]);

  useEffect(() => {
    if (!data?.players) return;
    const { goals: rebuilt, dropped } = goalsFromTotals(data.players);
    setGoals(rebuilt);
    setMotmTouched(false);
    setNotice(
      dropped
        ? `${dropped} assist${dropped === 1 ? '' : 's'} from an earlier version couldn't be matched to a goal and ${
            dropped === 1 ? 'was' : 'were'
          } dropped. Every assist now belongs to a goal.`
        : ''
    );
  }, [data]);

  const players = data?.players || [];
  const teamA = useMemo(() => players.filter((p) => p.team === 'A'), [players]);
  const teamB = useMemo(() => players.filter((p) => p.team === 'B'), [players]);

  const scoreA = goals.filter((g) => g.team === 'A').length;
  const scoreB = goals.filter((g) => g.team === 'B').length;

  const tally = useMemo(() => {
    const t = {};
    players.forEach((p) => (t[p.id] = { goals: 0, assists: 0, performance: p.performance ?? null }));
    goals.forEach((g) => {
      if (g.scorerId && t[g.scorerId]) t[g.scorerId].goals += 1;
      if (g.assistId && t[g.assistId]) t[g.assistId].assists += 1;
    });
    return t;
  }, [players, goals]);

  const suggested = useMemo(() => suggestManOfTheMatch(players, tally), [players, tally]);
  const effectiveMotmId = motmTouched ? motmId : suggested?.id ?? null;

  const addGoal = ({ scorerId, assistId }) => {
    setGoals((prev) => [...prev, newGoal(addingFor, scorerId, assistId)]);
    setAddingFor(null);
  };

  const removeGoal = (key) => setGoals((prev) => prev.filter((g) => g.key !== key));

  const nameOf = (id) => players.find((p) => p.id === id)?.name;

  const wasCompleted = data?.game?.status === 'completed';

  const submit = async () => {
    if (!data?.game || !isAdmin) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const motmPlayer = players.find((p) => p.id === effectiveMotmId) || null;

      await q(
        supabase.from('game_participants').upsert(
          players.map((p) => ({
            id: p.id,
            game_id: data.game.id,
            member_id: p.memberId,
            team: p.team,
            position: p.position,
            goals: tally[p.id]?.goals || 0,
            assists: tally[p.id]?.assists || 0,
            // Carried through untouched: an edit must not invent a rating, and
            // must not erase one an older version of the app recorded.
            performance: tally[p.id]?.performance ?? null,
          })),
          { onConflict: 'id' }
        ),
        'Saving each player’s stats'
      );

      await q(
        supabase
          .from('games')
          .update({
            status: 'completed',
            score_a: scoreA,
            score_b: scoreB,
            man_of_the_match_id: motmPlayer ? motmPlayer.memberId : null,
          })
          .eq('id', data.game.id),
        'Marking the game as played'
      );

      // An edit leaves a trail. game_edits is best-effort: a failure here means
      // the audit note is missing, not that the result didn't save.
      let auditWarning = null;
      if (wasCompleted) {
        const { error: auditErr } = await supabase.from('game_edits').insert({
          game_id: data.game.id,
          edit_type: 'result',
          note: `Result corrected to ${scoreA}–${scoreB}`,
          edited_by: user.id,
        });
        if (auditErr) auditWarning = `Result saved, but the edit couldn't be logged: ${auditErr.message}`;
      }

      // Ratings are derived by replaying the whole history rather than nudged
      // in place, which is what lets an old game be corrected at all.
      let ratingWarning = null;
      try {
        await recomputeGroupRatings(group.id);
      } catch (err) {
        ratingWarning = `Result saved, but ratings couldn't be recalculated: ${explain(err)}`;
      }

      setDone({
        scoreA,
        scoreB,
        motm: motmPlayer,
        edited: wasCompleted,
        warning: [auditWarning, ratingWarning].filter(Boolean).join(' ') || null,
      });
    } catch (err) {
      setSubmitError(explain(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page"><Spinner label="Loading the game…" /></div>;

  if (error) {
    return (
      <div className="page">
        <h1 className="page__title">FULL TIME</h1>
        <ErrorBanner error={error} onRetry={reload} />
      </div>
    );
  }

  if (done) {
    return (
      <div className="page">
        <h1 className="page__title">FULL TIME</h1>
        <div className="done">
          <Icon name="trophy" size={34} className="done__icon" />
          <p className="done__score">
            {done.scoreA} – {done.scoreB}
          </p>
          <p className="done__text">
            {done.edited ? 'Result updated. ' : ''}
            {done.scoreA === done.scoreB
              ? 'Honours even.'
              : `Team ${done.scoreA > done.scoreB ? 'A' : 'B'} takes it.`}
            {done.motm ? ` ⭐ ${done.motm.name} was man of the match.` : ''}
          </p>
          <p className="done__note">
            Every player's rating has been recalculated from the full history.
          </p>
          {done.warning && <p className="form-error">{done.warning}</p>}
          <div className="row row--gap">
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                setDone(null);
                setParams({});
              }}
            >
              {done.edited ? 'Done' : 'Log another'}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => navigate(`/g/${group.id}/history`)}>
              See history
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.game) {
    return (
      <div className="page">
        <h1 className="page__title">FULL TIME</h1>
        <EmptyState
          icon="clipboard"
          text="No teams to log yet — balance today's game first and hit Save teams."
          action={
            <button type="button" className="btn btn--primary" onClick={() => navigate(`/g/${group.id}/balance`)}>
              Go to Balance
            </button>
          }
        />
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className="page">
        <h1 className="page__title">FULL TIME</h1>
        <EmptyState
          icon="alert"
          text="There's a game waiting, but nobody was saved into it. Go back to Balance and press Save teams."
          action={
            <button type="button" className="btn btn--primary" onClick={() => navigate(`/g/${group.id}/balance`)}>
              Go to Balance
            </button>
          }
        />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <h1 className="page__title">FULL TIME</h1>
        <EmptyState
          icon="lock"
          text={`${players.length} players are lined up for today's game, but only group admins can submit the result.`}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page__title">FULL TIME</h1>

      <div className="scorecard">
        <div className="scorecard__side">
          <span className="scorecard__label scorecard__label--a">TEAM A</span>
          <span className="scorecard__value">{scoreA}</span>
          <button
            type="button"
            className="scorecard__add"
            onClick={() => setAddingFor('A')}
            aria-label="Add a goal for Team A"
          >
            <Icon name="plus" size={18} />
          </button>
        </div>

        <span className="scorecard__dash">–</span>

        <div className="scorecard__side">
          <span className="scorecard__label scorecard__label--b">TEAM B</span>
          <span className="scorecard__value">{scoreB}</span>
          <button
            type="button"
            className="scorecard__add"
            onClick={() => setAddingFor('B')}
            aria-label="Add a goal for Team B"
          >
            <Icon name="plus" size={18} />
          </button>
        </div>
      </div>
      <p className="hint hint--center">Add each goal as it happened — the score counts itself.</p>

      {notice && (
        <div className="banner banner--good">
          <Icon name="alert" size={18} className="banner__icon" />
          <div className="banner__body">
            <p className="banner__detail banner__detail--plain">{notice}</p>
          </div>
        </div>
      )}

      {goals.length > 0 && (
        <ul className="goals">
          {goals.map((g) => (
            <li key={g.key} className={`goal goal--${g.team.toLowerCase()}`}>
              <span className="goal__team">{g.team}</span>
              <span className="goal__text">
                {g.scorerId ? nameOf(g.scorerId) : <em>Own goal / unclaimed</em>}
                {g.assistId && <span className="goal__assist"> — assisted by {nameOf(g.assistId)}</span>}
              </span>
              <button
                type="button"
                className="icon-btn icon-btn--danger"
                onClick={() => removeGoal(g.key)}
                aria-label="Remove this goal"
              >
                <Icon name="x" size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {submitError && <ErrorBanner error={submitError} />}

      <div className="log-teams">
        {[
          { label: 'TEAM A', squad: teamA, variant: 'a' },
          { label: 'TEAM B', squad: teamB, variant: 'b' },
        ].map((t) => (
          <section key={t.label}>
            <h2 className={`team-heading team-heading--${t.variant}`}>{t.label}</h2>
            <div className="stack stack--tight">
              {t.squad.map((p) => (
                <PlayerCard
                  key={p.id}
                  player={p}
                  goals={tally[p.id]?.goals || 0}
                  assists={tally[p.id]?.assists || 0}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="motm">
        <h2 className="team-heading">MAN OF THE MATCH</h2>
        <p className="hint">
          {suggested && !motmTouched
            ? 'Suggested from goals and assists — change it if you saw it differently. The group votes on History either way, and their votes beat this pick.'
            : 'Pick whoever deserved it. The group can outvote you on History.'}
        </p>
        <div className="motm__grid">
          {players.map((p) => (
            <button
              type="button"
              key={p.id}
              className={`motm__chip ${effectiveMotmId === p.id ? 'motm__chip--on' : ''}`}
              onClick={() => {
                setMotmTouched(true);
                setMotmId(effectiveMotmId === p.id ? null : p.id);
              }}
            >
              {effectiveMotmId === p.id && <Icon name="star" size={13} />}
              {p.name}
            </button>
          ))}
        </div>
      </section>

      <button type="button" className="btn btn--primary btn--block btn--lg" onClick={submit} disabled={submitting}>
        {submitting ? 'Saving…' : 'Submit result'}
      </button>

      {addingFor && (
        <GoalModal
          teamLabel={`Team ${addingFor}`}
          players={addingFor === 'A' ? teamA : teamB}
          onAdd={addGoal}
          onCancel={() => setAddingFor(null)}
        />
      )}
    </div>
  );
}
